import { ChangeDetectorRef, Component, EventEmitter, Input, NgZone, Output } from '@angular/core';
import { inferContentType, MapsUploadService, UPLOAD_SIZE_LIMIT_BYTES } from '../../services/maps-upload.service';
import type { MapsUploadDoc } from '../../models/maps.types';

export interface UploadChip {
    /** Stable local key for rendering; either the server's uploadId or a transient client id. */
    localId: string;
    fileName: string;
    sizeBytes: number;
    status: 'uploading' | 'extracted' | 'failed';
    /** Short reason code (raw) — used for grouping. */
    extractError: string | null;
    /** Human-friendly explanation shown in the chip when status === 'failed'. */
    failureLabel: string | null;
    /** Server-side uploadId once finalize completes — emitted upstream when extracted. */
    uploadId: string | null;
}

const FRIENDLY_ERRORS: Record<string, string> = {
    unsupported_type: 'Unsupported file type — use .txt, .pdf, or .docx',
    size_exceeded: 'File is over the 10 MB limit',
    upload_register_failed: 'Server rejected the upload request',
    blob_put_failed: 'File transfer to storage failed (check CORS on the storage account)',
    finalize_failed: 'Server could not finalize the upload',
    pdf_parse_failed: 'PDF could not be read — try re-exporting',
    docx_parse_failed: 'DOCX could not be read — try re-saving from Word',
    txt_decode_failed: 'Text file could not be decoded',
    empty_text: 'File appears to have no readable text',
    blob_missing: 'File never reached storage',
    UPLOAD_TYPE_REJECTED: 'Unsupported file type — use .txt, .pdf, or .docx',
    UPLOAD_SIZE_EXCEEDED: 'File is over the 10 MB limit',
};

function friendlyError(reason: string | null | undefined): string {
    if (!reason) return 'Upload failed';
    return FRIENDLY_ERRORS[reason] ?? `Upload failed (${reason})`;
}

const MAX_FILES = 4;

@Component({
    selector: 'maps-upload-drop-zone',
    standalone: false,
    templateUrl: './upload-drop-zone.component.html',
    styleUrl: './upload-drop-zone.component.scss',
})
export class UploadDropZoneComponent {
    @Input() disabled = false;

    /** Emitted whenever the set of successfully-extracted uploadIds changes. */
    @Output() extractedUploadIdsChange = new EventEmitter<string[]>();
    /** Emitted whenever any chip is still in the `uploading` state — search button disables on this. */
    @Output() busyChange = new EventEmitter<boolean>();
    /** Emitted with the total attached-file count (toward the 4-file cap). */
    @Output() fileCountChange = new EventEmitter<number>();

    chips: UploadChip[] = [];
    isDragOver = false;
    readonly maxFiles = MAX_FILES;

    constructor(
        private readonly uploads: MapsUploadService,
        private readonly zone: NgZone,
        private readonly cdr: ChangeDetectorRef,
    ) {}

    get isFull(): boolean {
        return this.chips.length >= MAX_FILES;
    }

    onDragOver(event: DragEvent): void {
        if (this.disabled) return;
        event.preventDefault();
        this.isDragOver = true;
    }

    onDragLeave(event: DragEvent): void {
        event.preventDefault();
        this.isDragOver = false;
    }

    async onDrop(event: DragEvent): Promise<void> {
        event.preventDefault();
        this.isDragOver = false;
        if (this.disabled) return;
        const files = Array.from(event.dataTransfer?.files ?? []);
        await this.addFiles(files);
    }

    async onFilePick(event: Event): Promise<void> {
        const input = event.target as HTMLInputElement;
        const files = Array.from(input.files ?? []);
        input.value = ''; // allow re-pick of same filename
        await this.addFiles(files);
    }

    remove(localId: string): void {
        this.chips = this.chips.filter((c) => c.localId !== localId);
        this.emit();
    }

    private async addFiles(files: File[]): Promise<void> {
        const room = MAX_FILES - this.chips.length;
        const accepted = files.slice(0, Math.max(0, room));
        const newChips: UploadChip[] = accepted.map((f) => this.preValidate(f));
        this.chips = [...this.chips, ...newChips];
        this.emit();
        // Only attempt server upload for chips that survived pre-validation.
        await Promise.all(
            accepted
                .map((file, ix) => ({ file, chip: newChips[ix] }))
                .filter((pair) => pair.chip.status === 'uploading')
                .map((pair) => this.runOne(pair.file, pair.chip.localId))
        );
    }

    /**
     * Catch oversize / wrong-extension files before they hit the network so
     * the chip shows a friendly explanation immediately. The server still
     * enforces both limits as a backstop (TRD §3.4).
     */
    private preValidate(file: File): UploadChip {
        const localId = `local-${Math.random().toString(36).slice(2, 10)}`;
        if (file.size > UPLOAD_SIZE_LIMIT_BYTES) {
            return {
                localId,
                fileName: file.name,
                sizeBytes: file.size,
                status: 'failed',
                extractError: 'size_exceeded',
                failureLabel: friendlyError('size_exceeded'),
                uploadId: null,
            };
        }
        if (!inferContentType(file)) {
            return {
                localId,
                fileName: file.name,
                sizeBytes: file.size,
                status: 'failed',
                extractError: 'unsupported_type',
                failureLabel: friendlyError('unsupported_type'),
                uploadId: null,
            };
        }
        return {
            localId,
            fileName: file.name,
            sizeBytes: file.size,
            status: 'uploading',
            extractError: null,
            failureLabel: null,
            uploadId: null,
        };
    }

    private async runOne(file: File, localId: string): Promise<void> {
        let doc: MapsUploadDoc;
        try {
            doc = await this.uploads.upload(file);
        } catch (err) {
            doc = {
                uploadId: `local-${Math.random().toString(36).slice(2, 10)}`,
                originalFileName: file.name,
                contentType: file.type,
                sizeBytes: file.size,
                extractStatus: 'extract_failed',
                extractedLength: null,
                extractError: (err as Error)?.message ?? 'unknown',
            };
        }
        // Re-enter the Angular zone so the chip update renders.
        this.zone.run(() => {
            const ix = this.chips.findIndex((c) => c.localId === localId);
            if (ix === -1) return;
            const ok = doc.extractStatus === 'extracted';
            const updated: UploadChip = {
                ...this.chips[ix],
                uploadId: doc.uploadId,
                status: ok ? 'extracted' : 'failed',
                extractError: doc.extractError,
                failureLabel: ok ? null : friendlyError(doc.extractError),
            };
            this.chips = [...this.chips.slice(0, ix), updated, ...this.chips.slice(ix + 1)];
            this.emit();
            this.cdr.detectChanges();
        });
    }

    private emit(): void {
        const extracted = this.chips
            .filter((c) => c.status === 'extracted' && c.uploadId)
            .map((c) => c.uploadId as string);
        this.extractedUploadIdsChange.emit(extracted);
        const busy = this.chips.some((c) => c.status === 'uploading');
        this.busyChange.emit(busy);
        this.fileCountChange.emit(this.chips.length);
    }

    formatSize(bytes: number | null): string {
        if (bytes == null) return '';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
        return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    }
}
